/**
 * Module dependencies.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { Dashboard } from './dashboard';
import { appRoutes } from 'app/routes';
import { compileMenu } from 'app/core/utils/menu';
import { MdOutlineSubscriptions } from 'react-icons/md';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { HiMiniUsers } from 'react-icons/hi2';
import { GiHealthNormal } from 'react-icons/gi';
import { FaNetworkWired } from 'react-icons/fa6';
import { IoBagCheck } from 'react-icons/io5';
import { MdCampaign } from 'react-icons/md';
import { TbCoinEuro } from 'react-icons/tb';

/**
 * Export `SkinServices` component.
 */

export function SkinServices(): JSX.Element {
  const [translate] = useTranslation();
  const params = useParams();
  const items = useMemo(
    () =>
      compileMenu([
        {
          key: 'subscriptions',
          pathname: appRoutes.dashboard.services.subscriptionsList,
          icon: <MdOutlineSubscriptions />,
          label: translate('subscriptions.menu.list'),
          children: !params?.subscriptionId
            ? undefined
            : [
                {
                  key: 'subscriptionDetailUsers',
                  pathname: resolveAppPath(appRoutes.dashboard.services.subscriptionsDetail.users, params),
                  icon: <HiMiniUsers />,
                  label: translate('subscriptions.menu.associatedUsers')
                },
                {
                  key: 'subscriptionDetailPharmacies',
                  pathname: resolveAppPath(appRoutes.dashboard.services.subscriptionsDetail.pharmacies.base, params),
                  icon: <GiHealthNormal />,
                  label: translate('subscriptions.menu.associatedPharmacies'),
                  children: !params?.pharmacyId
                    ? undefined
                    : [
                        {
                          key: 'subscriptionDetailPharmaciesConsents',
                          pathname: resolveAppPath(
                            appRoutes.dashboard.services.subscriptionsDetail.pharmacies.consents,
                            params
                          ),
                          icon: <HiMiniUsers />,
                          label: translate('subscriptions.menu.pharmacies.consents')
                        },
                        {
                          key: 'subscriptionDetailPharmaciesIntegrations',
                          pathname: resolveAppPath(
                            appRoutes.dashboard.services.subscriptionsDetail.pharmacies.integrations,
                            params
                          ),
                          icon: <FaNetworkWired />,
                          label: translate('subscriptions.menu.pharmacies.integrations')
                        }
                      ]
                }
              ]
        },
        {
          key: 'pharmacyPlans',
          pathname: resolveAppPath(appRoutes.dashboard.services.pharmacyPlans, params),
          icon: <GiHealthNormal />,
          label: translate('subscriptions.menu.pharmacyPlans')
        },
        {
          key: 'subscriptionPharmacies',
          pathname: resolveAppPath(appRoutes.dashboard.services.pharmacies.base, params),
          icon: <GiHealthNormal />,
          label: translate('subscriptions.menu.pharmacies.base'),
          children:
            !params?.pharmacyId || params.subscriptionId
              ? undefined
              : [
                  {
                    key: 'pharmaciesPlan',
                    pathname: resolveAppPath(appRoutes.dashboard.services.pharmacies.plan, params),
                    icon: <IoBagCheck />,
                    label: translate('subscriptions.menu.plan')
                  },
                  {
                    key: 'pharmaciesLedger',
                    pathname: resolveAppPath(appRoutes.dashboard.services.pharmacies.ledger, params),
                    icon: <TbCoinEuro />,
                    label: translate('subscriptions.menu.ledger')
                  },
                  {
                    key: 'pharmaciesCampaigns',
                    pathname: resolveAppPath(appRoutes.dashboard.services.pharmacies.campaigns, params),
                    icon: <MdCampaign />,
                    label: translate('subscriptions.menu.campaigns')
                  },
                  {
                    key: 'pharmaciesConsents',
                    pathname: resolveAppPath(appRoutes.dashboard.services.pharmacies.consents, params),
                    icon: <HiMiniUsers />,
                    label: translate('subscriptions.menu.consents')
                  },
                  {
                    key: 'pharmaciesIntegrations',
                    pathname: resolveAppPath(appRoutes.dashboard.services.pharmacies.integrations, params),
                    icon: <FaNetworkWired />,
                    label: translate('subscriptions.menu.integrations')
                  },
                  {
                    key: 'pharmaciesSharedAccesses',
                    pathname: resolveAppPath(appRoutes.dashboard.services.pharmacies.sharedAccesses, params),
                    icon: <HiMiniUsers />,
                    label: translate('subscriptions.menu.sharedAccesses')
                  }
                ]
        },
        {
          key: 'pharmaciesUserStats',
          pathname: resolveAppPath(appRoutes.dashboard.services.pharmacies.userStats, params),
          icon: <HiMiniUsers />,
          label: translate('subscriptions.menu.pharmaciesUserStats')
        },
        {
          key: 'orders',
          pathname: resolveAppPath(appRoutes.dashboard.services.orders, params),
          icon: <IoBagCheck />,
          label: translate('subscriptions.menu.orders')
        },
        {
          key: 'sales',
          pathname: resolveAppPath(appRoutes.dashboard.services.sales, params),
          icon: <IoBagCheck />,
          label: translate('subscriptions.menu.sales')
        }
      ]),
    [params, translate]
  );

  return (
    <Dashboard menu={items}>
      <Outlet />
    </Dashboard>
  );
}
